"use client";

import { buttonVariants } from "@/components/atoms/buttons";
import { toast } from "@/components/atoms/toast";
import CartSheetProducts from "@/components/molecules/Cart/cart-sheet-products";
import OrderSummaryFooter from "@/components/templates/Checkout/components/order-summary-footer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useCart } from "@/hooks/useCart";
import { useCustomer } from "@/hooks/useCustomer";
import { getCartTotalQuantity } from "@/lib/cart";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { cn } from "@/lib/utils";
import { actions, segments } from "@/locales/segment";
import { ShoppingCart, ShoppingCartIcon } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";

interface CartButtonProps {
  locale: Locales;
}

export const isCartSegment = (segmentValue: string, locale: Locales): boolean => {
  const segment = segments[locale]?.cart;
  const localeActions = actions[locale];

  if (!segment || !localeActions) return false;

  return (
    segmentValue === segment ||
    segmentValue === localeActions["event-info"] ||
    segmentValue === localeActions["customer-info"] ||
    segmentValue === localeActions["delivery"] ||
    segmentValue === localeActions["recap"] ||
    segmentValue === localeActions["thank-you"]
  );
};

const CartButton = ({ locale }: CartButtonProps) => {
  const { customer } = useCustomer();
  const { cart } = useCart();
  const pathName = usePathname();
  const route = pathName.split("/").pop() || "";
  const itemCount = getCartTotalQuantity(cart?.items);

  const isCartEmpty = itemCount === 0;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          disabled={isCartSegment(route, locale)}
          className="xl:bg-blue-brand flex h-auto cursor-pointer items-center gap-2 bg-white p-0 transition-all duration-150 ease-in-out hover:scale-[102%] hover:!bg-none active:scale-[98%] disabled:opacity-50 xl:rounded-md xl:p-2.5 xl:hover:bg-[#007AAD]"
        >
          <div className="relative">
            <ShoppingCart className="hidden size-4 text-white xl:block" />
            <ShoppingCartIcon strokeWidth={1.5} className="text-dark-primary size-6.5 xl:hidden" />
            {itemCount > 0 && (
              <span className="bg-pink-primary absolute -top-0.5 -right-0.5 flex h-3 min-w-3 items-center justify-center rounded-full px-0.5 text-[8px] text-white disabled:bg-transparent xl:-top-1 xl:-right-1">
                {itemCount}
              </span>
            )}
          </div>
          <span className="font-cabinet-grotesk hidden text-sm leading-[150%] font-medium text-white xl:block">
            {__("Cart", locale)}
          </span>
        </button>
      </SheetTrigger>
      <SheetContent className="flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-cabinet-grotesk font-medium">{__("Your basket", locale)}</SheetTitle>
        </SheetHeader>

        <ul className="flex-1 overflow-hidden">
          <ScrollArea className="h-full px-4">
            <CartSheetProducts locale={locale} />
          </ScrollArea>
        </ul>
        <SheetFooter>
          <OrderSummaryFooter locale={locale} />
          <Link
            href={getPermalink(locale, "cart")}
            className={cn(
              buttonVariants(),
              "font-cabinet-grotesk py-3 text-sm font-bold transition-transform duration-150 ease-in-out active:scale-95 md:text-base"
            )}
          >
            {__("View cart", locale)}
          </Link>
          {!customer ? (
            <Link
              href={`${getPermalink(locale, "login")}?redirect=${getPermalink(locale, "checkout", "event-info")}`}
              className={cn(
                buttonVariants({ variant: "secondary" }),
                "bg-dark-primary text-white-primary !font-cabinet-grotesk min-h-11 py-3 text-center text-sm font-bold transition-transform duration-150 ease-in-out active:scale-95 md:text-base"
              )}
            >
              {__("Login", locale)}
            </Link>
          ) : (
            <Link
              href={getPermalink(locale, "checkout", "event-info")}
              onClick={(e) => {
                if (isCartEmpty) {
                  e.preventDefault();
                  toast.warning({ title: __("Your cart is empty. Please add items before proceeding.", locale) });
                }
              }}
              className={cn(
                buttonVariants({ variant: "secondary", size: "md" }),
                "bg-dark-primary min-h-11 text-sm font-semibold text-white transition-transform duration-150 ease-in-out active:scale-95 md:h-12 md:text-base"
              )}
            >
              {__("Next", locale)}
            </Link>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default CartButton;
